import React, { useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";


const salesTrendData = [
  { month: "Oct \'25", ncr: 4200000, luzon: 2800000, visayas: 1900000, mindanao: 1600000 },
  { month: "Nov \'25", ncr: 4800000, luzon: 3100000, visayas: 2100000, mindanao: 1750000 },
  { month: "Dec \'25", ncr: 5600000, luzon: 3700000, visayas: 2500000, mindanao: 2100000 },
  { month: "Jan \'26", ncr: 4900000, luzon: 3200000, visayas: 2200000, mindanao: 1850000 },
  { month: "Feb \'26", ncr: 5100000, luzon: 3400000, visayas: 2350000, mindanao: 1950000 },
  { month: "Mar \'26", ncr: 5450000, luzon: 3650000, visayas: 2480000, mindanao: 2080000 },
];

const LINES = [
  { key: "ncr", label: "NCR", color: "#DC2626" },
  { key: "luzon", label: "Luzon", color: "#D4AF37" },
  { key: "visayas", label: "Visayas", color: "#2563EB" },
  { key: "mindanao", label: "Mindanao", color: "#059669" },
];

const formatPHP = (v) => `₱${(v / 1000000)?.toFixed(1)}M`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg min-w-[160px]">
      <p className="font-caption text-xs font-semibold mb-2" style={{ color: '#0F172A' }}>{label}</p>
      {payload?.map((p) => (
        <div key={p?.dataKey} className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p?.color }} />
            <span className="font-caption text-xs" style={{ color: '#475569' }}>{p?.name}</span>
          </div>
          <span className="font-data text-xs font-medium" style={{ color: '#1E293B' }}>{formatPHP(p?.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function SalesTrendChart() {
  const [activeLines, setActiveLines] = useState(LINES?.map((l) => l?.key));

  const toggleLine = (key) => {
    setActiveLines((prev) =>
      prev?.includes(key) ? prev?.filter((k) => k !== key) : [...prev, key]
    );
  };

  return (
    <div className="card p-4 md:p-6 chart-panel-depth">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-lg md:text-xl font-semibold" style={{ color: '#0F172A', letterSpacing: '0.01em' }}>Sales Trend</h3>
          <p className="font-caption text-sm mt-0.5" style={{ color: '#475569' }}>6-month revenue by territory</p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {LINES?.map((line) => (
            <button
              key={line?.key}
              onClick={() => toggleLine(line?.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full font-caption text-xs font-medium transition-all duration-200 border ${
                activeLines?.includes(line?.key)
                  ? "border-transparent text-white" : "border-border bg-transparent"
              }`}
              style={{
                ...(activeLines?.includes(line?.key) ? { backgroundColor: line?.color } : { color: '#475569' })
              }}
            >
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: activeLines?.includes(line?.key) ? "white" : line?.color }}
              />
              {line?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full chart-height-standard" aria-label="Sales Trend Line Chart by Territory">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={salesTrendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.6} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fontFamily: "var(--font-caption)", fill: "#374151", fontWeight: 600 }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatPHP}
              tick={{ fontSize: 11, fontFamily: "var(--font-data)", fill: "#374151", fontWeight: 600 }}
              width={52}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            {LINES?.map((line) =>
              activeLines?.includes(line?.key) ? (
                <Line
                  key={line?.key}
                  type="monotone"
                  dataKey={line?.key}
                  name={line?.label}
                  stroke={line?.color}
                  strokeWidth={2.5}
                  dot={{ r: 5, fill: "#FFFFFF", stroke: line?.color, strokeWidth: 2.5 }}
                  activeDot={{ r: 7, fill: line?.color, stroke: "#FFFFFF", strokeWidth: 2 }}
                />
              ) : null
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}